import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Step1Schema, type Step1Input } from "../../../shared/schemas/step1";

import { Stack } from "@mui/material";
import { OnboardingStepLayout, TextInput, SelectInput, CountrySelect } from "./../../../shared/ui/index";
import { useTranslation } from "react-i18next";

type Step1FormValues = Step1Input;

type Step1Props = {
  currentStep: number;
  totalSteps: number;
  initialValues?: Step1FormValues;
  onCompleted: (data: Step1FormValues) => void;
};

export default function Step1_BasicInfo({
  currentStep,
  totalSteps,
  initialValues,
  onCompleted,
}: Step1Props) {
  const methods = useForm<Step1FormValues>({
    resolver: zodResolver(Step1Schema),
    defaultValues:
      initialValues ??
      ({
        country: "PT",
        companyName: "",
        fleetSize: undefined,
      } as unknown as Step1FormValues),
  });

  const { handleSubmit } = methods;
  const { t } = useTranslation();

  const onSubmit = handleSubmit((data) => {
    const clientId = `client_local_${Date.now()}`;
    sessionStorage.setItem("clientId", clientId);
    sessionStorage.setItem("step1", JSON.stringify(data));

    onCompleted(data);
  });

  return (
    <FormProvider {...methods}>
      <OnboardingStepLayout
        step={currentStep}
        totalSteps={totalSteps}
        title={t("onboarding.step1.title")}
        subtitle={t("onboarding.step1.subtitle")}
        onNext={onSubmit}
        nextLabel={t("onboarding.step1.actions.next")}
      >
        <Stack spacing={2.5}>
          {/* solo Portogallo */}
          <CountrySelect<Step1FormValues, "country">
            name="country"
            label={t("onboarding.step1.country.label")}
            required
            tooltip={t("onboarding.step1.country.tooltip")}
            disabled
            allowedCountryCodes={["PT"]}
          />

          <TextInput<Step1FormValues, "companyName">
            name="companyName"
            label={t("onboarding.step1.companyName.label")}
            placeholder={t("onboarding.step1.companyName.placeholder")}
            required
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />

          <SelectInput<Step1FormValues, "fleetSize">
            name="fleetSize"
            label={t("onboarding.step1.fleetSize.label")}
            placeholder={t("onboarding.step1.fleetSize.placeholder")}
            options={[
              { value: "1-5", label: "1 – 5" },
              { value: "5-20", label: "5 – 20" },
              { value: "20-50", label: "20 – 50" },
              { value: ">50", label: "> 50" },
            ]}
          />
        </Stack>
      </OnboardingStepLayout>
    </FormProvider>
  );
}
